'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { apiBase } from '@/lib/api';
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { AlertDialog } from '@/components/ConfirmDialog';
import Image from 'next/image';

type MembershipType = 'free' | 'paid_female' | 'paid_male';
type Gender = 'male' | 'female' | 'other';

type Category = { id: string; name: string };

// 目的: GET /api/market/orders/claim-info のレスポンス形を明示し、
// バックエンドの形状変更を型チェックで検知できるようにする
type ClaimInfoResponse = { orderNumber: string; email: string | null };

const SNS_PLATFORMS = ['Instagram', 'Facebook', 'Web', 'LINE', 'X'];

type FieldErrors = Record<string, string[]>;

const BASIC_FIELDS = [
  'membershipType',
  'email',
  'password',
  'passwordConfirm', // フロント専用（APIには送らない）
  'displayName',
  'gender',
  'introducer',
] as const;

const PROFILE_FIELDS = [
  'profile',
  'title',
  'photo',
  'offerings',
  'wantedConnections',
  'achievements',
  'links',
  'categoryIds',
] as const;

const FIELD_LABELS: Record<string, string> = {
  membershipType: '会員種別',
  email: 'メールアドレス',
  password: 'パスワード',
  passwordConfirm: 'パスワード（確認）',
  displayName: 'お名前',
  gender: '性別',
  introducer: '紹介者',
  profile: '自己紹介・プロフィール',
  title: '肩書き',
  photo: 'プロフィール画像',
  offerings: '提供できること',
  wantedConnections: 'こんな人と繋がりたい',
  achievements: '実績・活動紹介',
  links: 'SNS・外部リンク',
  categoryIds: 'カテゴリ',
};

/**
 * 会員種別ごとの「できる/できない」一覧（画像の表を元に作成）
 * 目的: 登録前に利用できる内容を明確にする
 */
const MEMBERSHIP_FEATURE_ROWS: Array<{
  label: string;
  paid_female: string;
  free: string; // 女性無料会員（既存の free を流用）
  paid_male: string;
}> = [
  {
    label: 'お茶会参加',
    paid_female: '3000円',
    free: '3000円',
    paid_male: '3000円',
  },
  {
    label: 'プチセミナー講師',
    paid_female: '○',
    free: '×',
    paid_male: '○',
  },
  {
    label: '結会HPにてメンバー紹介',
    paid_female: '○',
    free: '×',
    paid_male: '○',
  },
  {
    label: 'インタビュー動画撮影',
    paid_female: '22000円',
    free: '×',
    paid_male: '22000円',
  },
  {
    label: '販売促進会',
    paid_female: '主宰○　参加○',
    free: '主宰×　参加○',
    paid_male: '主宰○　参加×',
  },
  {
    label: 'オンラインショップ掲載',
    paid_female: '○',
    free: '×',
    paid_male: '○',
  },
  {
    label: '部活',
    paid_female: '部長○　部員○',
    free: '部長×　部員○',
    paid_male: '部長○　部員○',
  },
  {
    label: '求人サポート',
    paid_female: '募集○　応募○',
    free: '募集×　応募○',
    paid_male: '募集○　応募○',
  },
];

export default function RegisterForm() {
  const { login } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  // 目的: 特別URL（例: /register?plan=paid_male&bypass=...）で審査スキップを可能にする
  const approvalBypassToken = searchParams.get('bypass');
  // 目的: ゲスト購入後の完了画面から遷移した場合、注文の引き継ぎトークンを受け取る
  // 初回レンダー時点のURLから一度だけ読み取る（直後にURLからは取り除くため、以後は再読み取りしない）
  const [guestClaimToken] = useState<string | null>(() => searchParams.get('claim'));
  const [claimInfo, setClaimInfo] = useState<{
    orderNumber: string;
    email: string | null;
  } | null>(null);
  const [step, setStep] = useState<'type' | 'basic' | 'profile'>('type');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  // 目的: ゲスト購入からの遷移で「そのメールアドレスは既に会員登録済み」だった場合、
  // 「登録できず注文が孤立する」のを防ぐため、ログインへの導線を案内する。
  // （既存会員が注文時にログアウトしていた場合の救済 — レビュー指摘 #1b）
  const [duplicateEmailWithClaim, setDuplicateEmailWithClaim] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successRedirect, setSuccessRedirect] = useState('/');
  const [allCategories, setAllCategories] = useState<Category[]>([]);

  const [membershipType, setMembershipType] = useState<MembershipType | ''>('');
  const [form, setForm] = useState({
    // 基本情報
    email: '',
    password: '',
    passwordConfirm: '',
    displayName: '',
    gender: '' as Gender | '',
    profile: '',
    introducer: '',
    // プロフィール詳細
    title: '',
    photoId: null as string | null,
    photoUrl: null as string | null,
    offerings: [] as Array<{ title: string; description: string }>,
    wantedConnections: [] as Array<{ title: string; description: string }>,
    achievements: [] as Array<{
      date: string;
      title: string;
      description: string;
    }>,
    links: [] as Array<{ platform: string; url: string }>,
    categoryIds: [] as string[],
  });

  useEffect(() => {
    // カテゴリ一覧を取得（公開APIがあれば）
    fetch(`${apiBase()}/api/member-categories`)
      .then((res) => res.json())
      .then((data) => {
        if (data.items) {
          setAllCategories(data.items);
        }
      })
      .catch(() => {
        // カテゴリ取得失敗は無視（任意項目のため）
      });
  }, []);

  const handleMembershipTypeSelect = useCallback((type: MembershipType) => {
    setError(null);
    setFieldErrors({});
    setMembershipType(type);
    setStep('basic');
    if (type === 'paid_female') {
      setForm((prev) => ({ ...prev, gender: 'female' }));
    } else if (type === 'paid_male') {
      setForm((prev) => ({ ...prev, gender: 'male' }));
    } else if (type === 'free') {
      // 画像の表に合わせて「女性無料会員」として扱う
      setForm((prev) => ({ ...prev, gender: 'female' }));
    }
  }, []);

  // 目的: 男性会員説明ページなどから `?plan=paid_male` で遷移したとき、会員種別を自動選択して入力へ進める
  // 想定される例外: plan が不正値 / 既に手動選択済み / 既に別ステップへ進んでいる
  useEffect(() => {
    try {
      const plan = searchParams.get('plan');
      if (!plan) return;
      if (step !== 'type') return;
      if (membershipType) return;
      if (plan !== 'free' && plan !== 'paid_female' && plan !== 'paid_male') {
        return;
      }
      handleMembershipTypeSelect(plan);
    } catch (err: unknown) {
      // 目的: クエリパラメータ解析失敗時に、ユーザーの操作は継続できるようにする
      console.error('プラン選択の自動反映に失敗しました。', err);
    }
  }, [searchParams, step, membershipType, handleMembershipTypeSelect]);

  // 目的: claim トークンをアドレスバー・閲覧履歴・Refererヘッダーに残さないため、
  // 読み取り後すぐにURLから取り除く（トークン自体は上記 state から送信時の付与に引き継ぐ）。
  // 決済完了画面と同様のセキュリティ対応として追加（タスクブリーフ由来ではない）。
  // 想定される例外: リロード後は URL にトークンが残っていないため、通常の登録フォームにフォールバックする。
  useEffect(() => {
    if (!guestClaimToken) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('claim');
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    } catch (err: unknown) {
      // 目的: URL操作に失敗しても画面表示は継続する（トークンは既に state にある）
      console.error('URLからのトークン除去に失敗しました。', err);
    }
  }, [guestClaimToken]);

  // 目的: ゲスト注文の引き継ぎ対象を確認し、メールアドレスを自動入力する
  // 想定される例外: トークンが無効/期限切れ/使用済みなどの場合はAPIが404を返す（詳細区分はUIで判別しない）
  useEffect(() => {
    if (!guestClaimToken) return;
    let cancelled = false;
    fetch(
      `${apiBase()}/api/market/orders/claim-info?token=${encodeURIComponent(guestClaimToken)}`,
      { headers: { Accept: 'application/json' } },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((data: ClaimInfoResponse | null) => {
        if (cancelled || !data) return;
        setClaimInfo({ orderNumber: data.orderNumber, email: data.email ?? null });
        if (data.email) {
          const email = data.email;
          // 既に入力済みなら上書きしない
          setForm((prev) => (prev.email ? prev : { ...prev, email }));
        }
      })
      .catch(() => {
        if (!cancelled) setClaimInfo(null);
      });
    return () => {
      // 目的: アンマウント後（画面遷移後）に非同期レスポンスが state を更新しないようにする
      cancelled = true;
    };
  }, [guestClaimToken]);

  /**
   * セクション単位の入力チェック（フロント側）
   * 目的: 「基本情報の誤りは基本情報で直す」導線にする
   * 戻り値: フィールド別エラー（空ならOK）
   */
  const validateBasic = (): FieldErrors => {
    const errors: FieldErrors = {};
    if (!membershipType) {
      errors.membershipType = ['会員種別を選択してください。'];
    }
    if (!form.email) {
      errors.email = ['メールアドレスを入力してください。'];
    }
    if (!form.password) {
      errors.password = ['パスワードを入力してください。'];
    } else if (form.password.length < 8) {
      errors.password = ['パスワードは8文字以上で入力してください。'];
    }
    if (!form.passwordConfirm) {
      errors.passwordConfirm = ['確認用パスワードを入力してください。'];
    } else if (form.password !== form.passwordConfirm) {
      errors.passwordConfirm = ['パスワードが一致しません。'];
    }
    if (!form.displayName) {
      errors.displayName = ['お名前を入力してください。'];
    }
    if (!form.gender) {
      errors.gender = ['性別を選択してください。'];
    }
    return errors;
  };

  /**
   * プロフィール詳細の入力チェック（フロント側）
   * 目的: プロフィール詳細内の必須項目をその場で修正させる
   * 戻り値: フィールド別エラー（空ならOK）
   */
  const validateProfile = (): FieldErrors => {
    const errors: FieldErrors = {};
    if (!form.profile || form.profile.trim().length === 0) {
      errors.profile = ['自己紹介・プロフィールを入力してください。'];
    }
    // 画像の事前チェック（バックエンドも同等のバリデーションを行う）
    const photoFile = (
      document.getElementById('photo-input') as HTMLInputElement | null
    )?.files?.[0];
    if (photoFile) {
      const maxBytes = 10 * 1024 * 1024;
      if (photoFile.size > maxBytes) {
        errors.photo = [
          'プロフィール画像は10MB以下でアップロードしてください。',
        ];
      }
    }
    return errors;
  };

  const renderSectionErrors = (fields: readonly string[]) => {
    const items = fields.flatMap((f) =>
      (fieldErrors?.[f] ?? []).map((m) => ({
        field: f,
        label: FIELD_LABELS[f] ?? f,
        message: m,
      })),
    );
    if (items.length === 0) return null;
    return (
      <div className='rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700'>
        <div className='flex items-start gap-2'>
          <AlertCircle size={16} className='mt-0.5 shrink-0' />
          <div className='min-w-0'>
            <p className='font-bold'>入力内容に不備があります</p>
            <ul className='mt-2 list-disc space-y-1 pl-5'>
              {items.map((it, idx) => (
                <li key={`${it.field}-${idx}`} className='wrap-break-word'>
                  <span className='font-semibold'>{it.label}：</span>
                  {it.message}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const handleBasicNext = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setDuplicateEmailWithClaim(false);
    const basicErrors = validateBasic();
    if (Object.keys(basicErrors).length > 0) {
      setError('入力内容が不正です');
      setFieldErrors(basicErrors);
      return;
    }
    setStep('profile');
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      // 一時的にアップロード（登録時にはまだユーザーが存在しないため、別のエンドポイントが必要）
      // ここでは簡易的にbase64で保存するか、登録APIで一緒に送信する
      const reader = new FileReader();
      reader.onloadend = () => {
        // 一時的にURLを保存（実際のアップロードは登録APIで行う）
        setForm((prev) => ({ ...prev, photoUrl: reader.result as string }));
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error(err);
      setError('画像の読み込みに失敗しました');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setDuplicateEmailWithClaim(false);

    // 仕様: 基本情報の不備は「基本情報」ステップで修正させる
    const basicErrors = validateBasic();
    if (Object.keys(basicErrors).length > 0) {
      setError('入力内容が不正です');
      setFieldErrors(basicErrors);
      setStep('basic');
      return;
    }

    const profileErrors = validateProfile();
    if (Object.keys(profileErrors).length > 0) {
      setError('入力内容が不正です');
      setFieldErrors(profileErrors);
      return;
    }

    setSubmitting(true);

    try {
      // 画像ファイルがある場合はFormDataで送信
      const photoFile = (
        document.getElementById('photo-input') as HTMLInputElement
      )?.files?.[0];
      const formData = new FormData();

      formData.append('email', form.email);
      formData.append('password', form.password);
      formData.append('displayName', form.displayName);
      formData.append('membershipType', membershipType);
      formData.append('gender', form.gender);
      formData.append('profile', form.profile || '');
      formData.append('introducer', form.introducer || '');
      formData.append('title', form.title || '');
      formData.append('offerings', JSON.stringify(form.offerings));
      formData.append('wantedConnections', JSON.stringify(form.wantedConnections));
      formData.append('achievements', JSON.stringify(form.achievements));
      formData.append('links', JSON.stringify(form.links));
      formData.append('categoryIds', JSON.stringify(form.categoryIds));
      if (approvalBypassToken) {
        formData.append('approvalBypassToken', approvalBypassToken);
      }
      if (guestClaimToken) {
        formData.append('guestClaimToken', guestClaimToken);
      }
      if (photoFile) {
        formData.append('photo', photoFile);
      }

      const res = await fetch(`${apiBase()}/api/auth/register`, {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        // バリデーションエラー（どの項目か分かるように表示）
        if (res.status === 422 && data?.errors) {
          setError(data?.message ?? '入力内容が不正です');
          setFieldErrors(data.errors as FieldErrors);
          const errorKeys = Object.keys(data.errors ?? {});
          const hasBasic = errorKeys.some((k) =>
            (BASIC_FIELDS as readonly string[]).includes(k),
          );
          setStep(hasBasic ? 'basic' : 'profile');

          // 目的: ゲスト購入からの遷移で、入力したメールアドレスが既に会員登録済みだった場合
          // （バックエンドの unique:users,email 制約によるエラー）、このまま登録を諦めさせず
          // 「ログインすれば注文を引き継げる」ことを案内する。
          // 判定: Laravel の unique バリデーションメッセージ（「このメールアドレスは既に
          // 使用されています。」）は required/email形式/max とは文言が異なり一意に判別できる。
          const emailErrors: string[] = (data.errors as FieldErrors)?.email ?? [];
          const isDuplicateEmail = emailErrors.some((m) =>
            m.includes('既に使用されています'),
          );
          if (guestClaimToken && isDuplicateEmail) {
            setDuplicateEmailWithClaim(true);
          }
          return;
        }
        throw new Error(data?.message || '登録に失敗しました。');
      }

      if (data.token) {
        await login({ email: form.email, password: form.password });
        setSuccessMessage('登録が完了しました。');
        setSuccessRedirect('/profile');
        setShowSuccessDialog(true);
      } else {
        setSuccessMessage(
          data?.message ??
            'お申し込みを受け付けました。\n\n【重要】運営による審査後、ご登録のメールアドレス宛に「決済リンク」をお送りします。決済完了後にアカウントが有効化されます。\n\n※ メールが届かない場合は迷惑メールフォルダもご確認ください。',
        );
        setSuccessRedirect('/');
        setShowSuccessDialog(true);
      }
    } catch (err: unknown) {
      console.error(err);
      const message =
        err instanceof Error ? err.message : '登録に失敗しました。';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'type') {
    return (
      <div className='min-h-screen bg-white'>
        <Header />
        <main className='bg-washi'>
          <section className='mx-auto max-w-7xl px-6 py-16'>
            <div className='mx-auto max-w-2xl'>
              <h1 className='text-center font-serif text-3xl text-gray-800 mb-4'>
                会員申し込み
              </h1>
              <p className='text-center text-gray-600 mb-8'>
                会員種別を選択してください
              </p>

              <div className='grid gap-6 md:grid-cols-1'>
                <div className='flex flex-col gap-6 border border-gray-200 bg-white p-6 shadow-sm md:flex-row md:items-center md:justify-between md:gap-10 md:p-8'>
                  <div className='min-w-0'>
                    <div className='flex flex-wrap items-baseline gap-x-4 gap-y-1'>
                      <div className='text-2xl font-bold text-gray-900'>
                        女性無料会員
                      </div>
                      <div className='text-sm font-bold text-primary'>無料</div>
                    </div>
                    <ul className='mt-4 space-y-2 text-sm text-gray-600'>
                      <li className='flex items-start gap-2'>
                        <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-secondary' />
                        <span>お茶会参加（3000円）</span>
                      </li>
                      <li className='flex items-start gap-2'>
                        <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-secondary' />
                        <span>販売促進会：参加○（主宰×）</span>
                      </li>
                      <li className='flex items-start gap-2'>
                        <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-secondary' />
                        <span>部活：部員○（部長×）</span>
                      </li>
                      <li className='flex items-start gap-2'>
                        <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-secondary' />
                        <span>求人サポート：応募○（募集×）</span>
                      </li>
                    </ul>
                  </div>
                  <div className='shrink-0'>
                    <button
                      type='button'
                      onClick={() => handleMembershipTypeSelect('free')}
                      className='inline-flex w-full items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-bold text-white shadow-md transition-all hover:bg-primary/90 md:w-auto'
                    >
                      このプランで申し込む
                    </button>
                  </div>
                </div>

                <div className='flex flex-col gap-6 border border-gray-200 bg-white p-6 shadow-sm md:flex-row md:items-center md:justify-between md:gap-10 md:p-8'>
                  <div className='min-w-0'>
                    <div className='flex flex-wrap items-baseline gap-x-4 gap-y-1'>
                      <div className='text-2xl font-bold text-gray-900'>
                        女性有料会員
                      </div>
                      <div className='text-sm font-bold text-primary'>
                        1,480円／月
                      </div>
                    </div>
                    <ul className='mt-4 space-y-2 text-sm text-gray-600'>
                      <li className='flex items-start gap-2'>
                        <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-secondary' />
                        <span>お茶会参加（3000円）</span>
                      </li>
                      <li className='flex items-start gap-2'>
                        <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-secondary' />
                        <span>プチセミナー講師○</span>
                      </li>
                      <li className='flex items-start gap-2'>
                        <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-secondary' />
                        <span>結会HPでメンバー紹介○</span>
                      </li>
                      <li className='flex items-start gap-2'>
                        <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-secondary' />
                        <span>部活：部長○ 部員○</span>
                      </li>
                      <li className='flex items-start gap-2'>
                        <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-secondary' />
                        <span>求人サポート：募集○ 応募○</span>
                      </li>
                    </ul>
                  </div>
                  <div className='shrink-0'>
                    <button
                      type='button'
                      onClick={() => handleMembershipTypeSelect('paid_female')}
                      className='inline-flex w-full items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-bold text-white shadow-md transition-all hover:bg-primary/90 md:w-auto'
                    >
                      このプランで申し込む
                    </button>
                  </div>
                </div>

                <div className='flex flex-col gap-6 border border-gray-200 bg-white p-6 shadow-sm md:flex-row md:items-center md:justify-between md:gap-10 md:p-8'>
                  <div className='min-w-0'>
                    <div className='flex flex-wrap items-baseline gap-x-4 gap-y-1'>
                      <div className='text-2xl font-bold text-gray-900'>
                        男性有料会員
                      </div>
                      <div className='text-sm font-bold text-primary'>
                        3,480円／月
                      </div>
                    </div>
                    <ul className='mt-4 space-y-2 text-sm text-gray-600'>
                      <li className='flex items-start gap-2'>
                        <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-secondary' />
                        <span>お茶会参加（3000円）</span>
                      </li>
                      <li className='flex items-start gap-2'>
                        <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-secondary' />
                        <span>結会HPでメンバー紹介○</span>
                      </li>
                      <li className='flex items-start gap-2'>
                        <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-secondary' />
                        <span>販売促進会：主宰○（参加×）</span>
                      </li>
                      <li className='flex items-start gap-2'>
                        <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-secondary' />
                        <span>インタビュー動画撮影（5000円）</span>
                      </li>
                      <li className='flex items-start gap-2'>
                        <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-secondary' />
                        <span>求人サポート：募集○ 応募○</span>
                      </li>
                    </ul>
                  </div>
                  <div className='shrink-0 flex flex-col gap-3 md:items-end'>
                    <button
                      type='button'
                      onClick={() => handleMembershipTypeSelect('paid_male')}
                      className='inline-flex w-full items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-bold text-white shadow-md transition-all hover:bg-primary/90 md:w-auto'
                    >
                      このプランで申し込む
                    </button>
                  </div>
                </div>
              </div>

              {/* できる / できない 一覧（画像の表） */}
              <div className='mt-10 border border-gray-200 bg-white shadow-sm'>
                <div className='px-5 py-4 border-b border-gray-200'>
                  <h2 className='font-serif text-lg font-bold text-gray-800'>
                    できること・できないこと（会員種別別）
                  </h2>
                  <p className='mt-1 text-xs text-gray-600'>
                    記号の意味：○=可 / ×=不可 / △=条件付き
                  </p>
                </div>
                <div className='overflow-x-auto'>
                  <table className='min-w-[760px] w-full border-collapse'>
                    <thead>
                      <tr className='bg-primary text-white'>
                        <th className='border border-white/40 px-4 py-3 text-left text-sm font-bold'>
                          内容
                        </th>
                        <th className='border border-white/40 px-4 py-3 text-center text-sm font-bold'>
                          女性有料会員
                        </th>
                        <th className='border border-white/40 px-4 py-3 text-center text-sm font-bold'>
                          女性無料会員
                        </th>
                        <th className='border border-white/40 px-4 py-3 text-center text-sm font-bold'>
                          男性有料会員
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {MEMBERSHIP_FEATURE_ROWS.map((row) => (
                        <tr
                          key={row.label}
                          className='odd:bg-white even:bg-gray-50'
                        >
                          <td className='border border-gray-200 px-4 py-3 text-sm text-gray-800'>
                            {row.label}
                          </td>
                          <td className='border border-gray-200 px-4 py-3 text-center text-sm font-bold text-gray-800 whitespace-nowrap'>
                            {row.paid_female}
                          </td>
                          <td className='border border-gray-200 px-4 py-3 text-center text-sm font-bold text-gray-800 whitespace-nowrap'>
                            {row.free}
                          </td>
                          <td className='border border-gray-200 px-4 py-3 text-center text-sm font-bold text-gray-800 whitespace-nowrap'>
                            {row.paid_male}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  if (step === 'basic') {
    return (
      <div className='min-h-screen bg-white'>
        <Header />
        <main className='bg-washi'>
          <section className='mx-auto max-w-7xl px-6 py-16'>
            <div className='mx-auto max-w-2xl border border-gray-100 bg-white p-6 shadow-sm'>
              <div className='mb-6 flex items-center justify-between'>
                <h1 className='font-serif text-2xl text-gray-800'>基本情報</h1>
                <button
                  onClick={() => setStep('type')}
                  className='text-sm text-gray-600 hover:text-gray-900'
                >
                  ← 戻る
                </button>
              </div>

              {claimInfo && (
                <div className='mb-6 rounded-none border border-line bg-gray-50 p-4 text-sm text-gray-700'>
                  ご注文 {claimInfo.orderNumber} を、この会員登録で引き継ぎます。
                  登録後は注文履歴からご確認いただけます。
                </div>
              )}

              <form className='space-y-4' onSubmit={handleBasicNext}>
                {renderSectionErrors(BASIC_FIELDS)}
                {duplicateEmailWithClaim && (
                  <div className='rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-gray-700'>
                    <p className='font-semibold'>
                      このメールアドレスは既に会員登録されています。
                    </p>
                    <p className='mt-1'>
                      会員の方は
                      <Link
                        href='/login'
                        className='mx-1 font-semibold text-primary underline hover:text-primary/80'
                      >
                        ログイン
                      </Link>
                      してください。ログイン後、今回のご注文を引き継げる場合があります。
                    </p>
                  </div>
                )}
                <div>
                  <label className='block text-sm font-semibold text-gray-800'>
                    メールアドレス <span className='text-rose-500'>*</span>
                  </label>
                  <input
                    className={`mt-2 w-full border px-3 py-3 text-[15px] ${
                      fieldErrors.email ? 'border-rose-400' : 'border-gray-200'
                    }`}
                    type='email'
                    value={form.email}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    required
                  />
                  {fieldErrors.email?.[0] ? (
                    <p className='mt-1 text-xs text-rose-600'>
                      {fieldErrors.email[0]}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className='block text-sm font-semibold text-gray-800'>
                    パスワード <span className='text-rose-500'>*</span>
                  </label>
                  <input
                    className={`mt-2 w-full border px-3 py-3 text-[15px] ${
                      fieldErrors.password
                        ? 'border-rose-400'
                        : 'border-gray-200'
                    }`}
                    type='password'
                    value={form.password}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, password: e.target.value }))
                    }
                    required
                    minLength={8}
                    placeholder='8文字以上'
                  />
                  {fieldErrors.password?.[0] ? (
                    <p className='mt-1 text-xs text-rose-600'>
                      {fieldErrors.password[0]}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className='block text-sm font-semibold text-gray-800'>
                    パスワード（確認） <span className='text-rose-500'>*</span>
                  </label>
                  <input
                    className={`mt-2 w-full border px-3 py-3 text-[15px] ${
                      fieldErrors.passwordConfirm
                        ? 'border-rose-400'
                        : 'border-gray-200'
                    }`}
                    type='password'
                    value={form.passwordConfirm}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        passwordConfirm: e.target.value,
                      }))
                    }
                    required
                    minLength={8}
                  />
                  {fieldErrors.passwordConfirm?.[0] ? (
                    <p className='mt-1 text-xs text-rose-600'>
                      {fieldErrors.passwordConfirm[0]}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className='block text-sm font-semibold text-gray-800'>
                    お名前 <span className='text-rose-500'>*</span>
                  </label>
                  <input
                    className={`mt-2 w-full border px-3 py-3 text-[15px] ${
                      fieldErrors.displayName
                        ? 'border-rose-400'
                        : 'border-gray-200'
                    }`}
                    type='text'
                    value={form.displayName}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        displayName: e.target.value,
                      }))
                    }
                    required
                  />
                  {fieldErrors.displayName?.[0] ? (
                    <p className='mt-1 text-xs text-rose-600'>
                      {fieldErrors.displayName[0]}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className='block text-sm font-semibold text-gray-800'>
                    性別 <span className='text-rose-500'>*</span>
                  </label>
                  <select
                    className={`mt-2 w-full border px-3 py-3 text-[15px] ${
                      fieldErrors.gender ? 'border-rose-400' : 'border-gray-200'
                    }`}
                    value={form.gender}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        gender: e.target.value as Gender,
                      }))
                    }
                    required
                    disabled={
                      membershipType === 'paid_female' ||
                      membershipType === 'paid_male' ||
                      membershipType === 'free'
                    }
                  >
                    <option value=''>選択してください</option>
                    <option value='female'>女性</option>
                    <option value='male'>男性</option>
                    <option value='other'>その他</option>
                  </select>
                  {fieldErrors.gender?.[0] ? (
                    <p className='mt-1 text-xs text-rose-600'>
                      {fieldErrors.gender[0]}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className='block text-sm font-semibold text-gray-800'>
                    紹介者
                  </label>
                  <input
                    className='mt-2 w-full border border-gray-200 px-3 py-3 text-[15px]'
                    type='text'
                    value={form.introducer}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        introducer: e.target.value,
                      }))
                    }
                    placeholder='紹介者のお名前（任意）'
                  />
                </div>

                <button
                  type='submit'
                  className='mt-4 inline-flex w-full items-center justify-center gap-2 bg-primary px-4 py-3 text-sm font-semibold text-white'
                >
                  次へ：プロフィール詳細を入力
                </button>
              </form>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-white'>
      <Header />
      <main className='bg-washi'>
        <section className='mx-auto max-w-7xl px-6 py-16'>
          <div className='mx-auto max-w-3xl border border-gray-100 bg-white p-6 shadow-sm'>
            <div className='mb-6 flex items-center justify-between'>
              <h1 className='font-serif text-2xl text-gray-800'>
                プロフィール詳細
              </h1>
              <button
                onClick={() => setStep('basic')}
                className='text-sm text-gray-600 hover:text-gray-900'
              >
                ← 戻る
              </button>
            </div>

            <div className='mb-4 rounded-lg bg-primary/10 p-3 text-sm text-gray-700'>
              {membershipType === 'free' && (
                <p>
                  女性無料会員として申し込みます。登録後すぐにご利用いただけます。
                </p>
              )}
              {(membershipType === 'paid_female' ||
                membershipType === 'paid_male') && (
                <div>
                  <p className='font-semibold'>有料会員のお申し込みとして受け付けます。</p>
                  <ol className='mt-2 list-inside list-decimal space-y-1 text-gray-600'>
                    <li>運営が申請内容を審査します</li>
                    <li>審査後、<strong>ご登録のメールアドレス宛</strong>に<strong>決済リンク</strong>をお送りします</li>
                    <li>決済完了後にアカウントが有効化されます</li>
                  </ol>
                  <p className='mt-2 text-xs text-gray-500'>※ メールが届かない場合は迷惑メールフォルダもご確認ください</p>
                </div>
              )}
            </div>

            <form className='space-y-6' onSubmit={handleSubmit}>
              {renderSectionErrors(PROFILE_FIELDS)}
              {/* プロフィール画像 */}
              <div>
                <label className='block text-sm font-semibold text-gray-800 mb-2'>
                  プロフィール画像
                </label>
                <div className='flex items-start gap-6'>
                  <div className='relative h-32 w-32 shrink-0 overflow-hidden rounded-full bg-gray-100 border border-gray-200'>
                    {form.photoUrl ? (
                      <Image
                        src={form.photoUrl}
                        alt='Profile'
                        fill
                        className='object-cover'
                      />
                    ) : (
                      <div className='flex h-full w-full items-center justify-center text-gray-400'>
                        <Upload size={24} />
                      </div>
                    )}
                  </div>
                  <div className='flex-1'>
                    <input
                      id='photo-input'
                      type='file'
                      accept='image/*'
                      onChange={handlePhotoUpload}
                      className='block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20'
                    />
                    {fieldErrors.photo?.[0] ? (
                      <p className='mt-2 text-xs text-rose-600'>
                        {fieldErrors.photo[0]}
                      </p>
                    ) : null}
                    <p className='mt-2 text-xs text-gray-500'>
                      推奨サイズ: 500x500px以上。10MB以下のJPG, PNG, GIF, WebP。
                    </p>
                  </div>
                </div>
              </div>

              {/* 肩書き */}
              <div>
                <label className='block text-sm font-semibold text-gray-800'>
                  肩書き
                </label>
                <input
                  className='mt-2 w-full border border-gray-200 px-3 py-3 text-[15px]'
                  type='text'
                  value={form.title}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder='例: Webデザイナー / 株式会社〇〇 代表'
                />
              </div>

              {/* 自己紹介 */}
              <div>
                <label className='block text-sm font-semibold text-gray-800'>
                  自己紹介・プロフィール{' '}
                  <span className='text-rose-500'>*</span>
                </label>
                <textarea
                  className={`mt-2 w-full border px-3 py-3 text-[15px] ${
                    fieldErrors.profile ? 'border-rose-400' : 'border-gray-200'
                  }`}
                  rows={6}
                  value={form.profile}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, profile: e.target.value }))
                  }
                  required
                  placeholder='自己紹介や活動内容などを記入してください'
                />
                {fieldErrors.profile?.[0] ? (
                  <p className='mt-1 text-xs text-rose-600'>
                    {fieldErrors.profile[0]}
                  </p>
                ) : null}
              </div>

              {/* カテゴリ */}
              {allCategories.length > 0 && (
                <div>
                  <label className='block text-sm font-semibold text-gray-800 mb-2'>
                    カテゴリ
                  </label>
                  <div className='flex flex-wrap gap-3'>
                    {allCategories.map((cat) => (
                      <label
                        key={cat.id}
                        className={`cursor-pointer rounded-full border px-3 py-1 text-sm transition-colors ${
                          form.categoryIds.includes(cat.id)
                            ? 'border-primary bg-primary/10 text-primary font-bold'
                            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type='checkbox'
                          className='sr-only'
                          checked={form.categoryIds.includes(cat.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setForm((prev) => ({
                                ...prev,
                                categoryIds: [...prev.categoryIds, cat.id],
                              }));
                            } else {
                              setForm((prev) => ({
                                ...prev,
                                categoryIds: prev.categoryIds.filter(
                                  (id) => id !== cat.id,
                                ),
                              }));
                            }
                          }}
                        />
                        {cat.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* 提供できること */}
              <div>
                <div className='flex items-center justify-between mb-2'>
                  <label className='block text-sm font-semibold text-gray-800'>
                    提供できること
                  </label>
                  <button
                    type='button'
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        offerings: [
                          ...prev.offerings,
                          { title: '', description: '' },
                        ],
                      }))
                    }
                    className='flex items-center gap-1 text-sm font-bold text-primary hover:text-primary/80'
                  >
                    <Plus size={16} /> 追加
                  </button>
                </div>
                <div className='space-y-3'>
                  {form.offerings.map((item, index) => (
                    <div
                      key={index}
                      className='relative rounded-lg border border-gray-100 bg-gray-50 p-4'
                    >
                      <button
                        type='button'
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            offerings: prev.offerings.filter(
                              (_, i) => i !== index,
                            ),
                          }))
                        }
                        className='absolute right-2 top-2 text-gray-400 hover:text-rose-500'
                      >
                        <Trash2 size={16} />
                      </button>
                      <div className='grid gap-3'>
                        <input
                          type='text'
                          placeholder='タイトル (例: Webサイト制作)'
                          value={item.title}
                          onChange={(e) => {
                            const newItems = [...form.offerings];
                            newItems[index].title = e.target.value;
                            setForm((prev) => ({
                              ...prev,
                              offerings: newItems,
                            }));
                          }}
                          className='w-full border border-gray-300 px-3 py-2 text-sm text-gray-900'
                        />
                        <textarea
                          placeholder='詳細説明'
                          value={item.description}
                          onChange={(e) => {
                            const newItems = [...form.offerings];
                            newItems[index].description = e.target.value;
                            setForm((prev) => ({
                              ...prev,
                              offerings: newItems,
                            }));
                          }}
                          className='w-full border border-gray-300 px-3 py-2 text-sm text-gray-900'
                          rows={2}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* こんな人と繋がりたい */}
              <div>
                <div className='flex items-center justify-between mb-2'>
                  <label className='block text-sm font-semibold text-gray-800'>
                    こんな人と繋がりたい
                  </label>
                  <button
                    type='button'
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        wantedConnections: [
                          ...prev.wantedConnections,
                          { title: '', description: '' },
                        ],
                      }))
                    }
                    className='flex items-center gap-1 text-sm font-bold text-primary hover:text-primary/80'
                  >
                    <Plus size={16} /> 追加
                  </button>
                </div>
                <div className='space-y-3'>
                  {form.wantedConnections.map((item, index) => (
                    <div
                      key={index}
                      className='relative rounded-lg border border-gray-100 bg-gray-50 p-4'
                    >
                      <button
                        type='button'
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            wantedConnections: prev.wantedConnections.filter(
                              (_, i) => i !== index,
                            ),
                          }))
                        }
                        className='absolute right-2 top-2 text-gray-400 hover:text-rose-500'
                      >
                        <Trash2 size={16} />
                      </button>
                      <div className='grid gap-3'>
                        <input
                          type='text'
                          placeholder='どんな方か (例: 地方で事業をしている方)'
                          value={item.title}
                          onChange={(e) => {
                            const newItems = [...form.wantedConnections];
                            newItems[index].title = e.target.value;
                            setForm((prev) => ({
                              ...prev,
                              wantedConnections: newItems,
                            }));
                          }}
                          className='w-full border border-gray-300 px-3 py-2 text-sm text-gray-900'
                        />
                        <textarea
                          placeholder='理由・してみたいこと'
                          value={item.description}
                          onChange={(e) => {
                            const newItems = [...form.wantedConnections];
                            newItems[index].description = e.target.value;
                            setForm((prev) => ({
                              ...prev,
                              wantedConnections: newItems,
                            }));
                          }}
                          className='w-full border border-gray-300 px-3 py-2 text-sm text-gray-900'
                          rows={2}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 実績・活動 */}
              <div>
                <div className='flex items-center justify-between mb-2'>
                  <label className='block text-sm font-semibold text-gray-800'>
                    実績・活動紹介
                  </label>
                  <button
                    type='button'
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        achievements: [
                          ...prev.achievements,
                          { date: '', title: '', description: '' },
                        ],
                      }))
                    }
                    className='flex items-center gap-1 text-sm font-bold text-primary hover:text-primary/80'
                  >
                    <Plus size={16} /> 追加
                  </button>
                </div>
                <div className='space-y-3'>
                  {form.achievements.map((item, index) => (
                    <div
                      key={index}
                      className='relative rounded-lg border border-gray-100 bg-gray-50 p-4'
                    >
                      <button
                        type='button'
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            achievements: prev.achievements.filter(
                              (_, i) => i !== index,
                            ),
                          }))
                        }
                        className='absolute right-2 top-2 text-gray-400 hover:text-rose-500'
                      >
                        <Trash2 size={16} />
                      </button>
                      <div className='grid gap-3'>
                        <div className='grid grid-cols-3 gap-3'>
                          <input
                            type='text'
                            placeholder='時期 (例: 2024年4月)'
                            value={item.date}
                            onChange={(e) => {
                              const newItems = [...form.achievements];
                              newItems[index].date = e.target.value;
                              setForm((prev) => ({
                                ...prev,
                                achievements: newItems,
                              }));
                            }}
                            className='col-span-1 border border-gray-300 px-3 py-2 text-sm text-gray-900'
                          />
                          <input
                            type='text'
                            placeholder='タイトル'
                            value={item.title}
                            onChange={(e) => {
                              const newItems = [...form.achievements];
                              newItems[index].title = e.target.value;
                              setForm((prev) => ({
                                ...prev,
                                achievements: newItems,
                              }));
                            }}
                            className='col-span-2 border border-gray-300 px-3 py-2 text-sm text-gray-900'
                          />
                        </div>
                        <textarea
                          placeholder='詳細説明'
                          value={item.description}
                          onChange={(e) => {
                            const newItems = [...form.achievements];
                            newItems[index].description = e.target.value;
                            setForm((prev) => ({
                              ...prev,
                              achievements: newItems,
                            }));
                          }}
                          className='w-full border border-gray-300 px-3 py-2 text-sm text-gray-900'
                          rows={2}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* SNS・外部リンク */}
              <div>
                <div className='flex items-center justify-between mb-2'>
                  <label className='block text-sm font-semibold text-gray-800'>
                    SNS・外部リンク
                  </label>
                  <button
                    type='button'
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        links: [
                          ...prev.links,
                          { platform: 'Instagram', url: '' },
                        ],
                      }))
                    }
                    className='flex items-center gap-1 text-sm font-bold text-primary hover:text-primary/80'
                  >
                    <Plus size={16} /> 追加
                  </button>
                </div>
                <div className='space-y-3'>
                  {form.links.map((link, index) => (
                    <div
                      key={index}
                      className='relative flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3'
                    >
                      <button
                        type='button'
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            links: prev.links.filter((_, i) => i !== index),
                          }))
                        }
                        className='absolute right-2 top-2 text-gray-400 hover:text-rose-500'
                      >
                        <X size={16} />
                      </button>
                      <select
                        value={link.platform}
                        onChange={(e) => {
                          const newItems = [...form.links];
                          newItems[index].platform = e.target.value;
                          setForm((prev) => ({ ...prev, links: newItems }));
                        }}
                        className='w-32 border border-gray-300 px-3 py-2 text-sm text-gray-900'
                      >
                        {SNS_PLATFORMS.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                      <input
                        type='url'
                        placeholder='URL'
                        value={link.url}
                        onChange={(e) => {
                          const newItems = [...form.links];
                          newItems[index].url = e.target.value;
                          setForm((prev) => ({ ...prev, links: newItems }));
                        }}
                        className='flex-1 border border-gray-300 px-3 py-2 text-sm text-gray-900'
                      />
                    </div>
                  ))}
                </div>
              </div>

              {error && (
                <div className='flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700'>
                  <AlertCircle size={16} />
                  <p>{error}</p>
                </div>
              )}

              <button
                type='submit'
                disabled={submitting}
                className='mt-4 inline-flex w-full items-center justify-center gap-2 bg-primary px-4 py-3 text-sm font-semibold text-white disabled:opacity-60'
              >
                {submitting ? (
                  <>
                    <Loader2 className='animate-spin' size={16} />
                    登録中...
                  </>
                ) : (
                  '登録する'
                )}
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer />

      <AlertDialog
        open={showSuccessDialog}
        title='登録完了'
        message={successMessage}
        variant='success'
        onClose={() => { setShowSuccessDialog(false); router.push(successRedirect); }}
      />
    </div>
  );
}
